import React, { useEffect, useState } from 'react';

import { LocalizationProvider } from '@mui/lab';
import {
    Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';

import Styles from '../lib/Styles';
import classes from './ScheduleDialog.module.scss';

interface IScheduleDialogProps {
  numCampaigns: number;
  showDialog: boolean;
  setShowDialog: Function;
  handleAgree: Function;
  handleDisagree: Function;
}

// Give Date an 'isValid' function.
const prototype: any = Date.prototype;
prototype.isValid = function () {
  // An invalid date object returns NaN for getTime() and NaN is the only
  // object not strictly equal to itself.
  // eslint-disable-next-line no-self-compare
  return this.getTime() === this.getTime();
};

/**
 * Returns a date instance with added `weeks` of delay.
 *
 * @param {Number} weeks - the number of weeks to add
 *
 * @returns {Date}
 */
function addWeeks(weeks: number, date: Date = new Date()) {
  date.setDate(date.getDate() + weeks * 7)
  return date
}

export default function ScheduleDialog(props: IScheduleDialogProps) {
  const cx = new Styles(classes).cx;
  const { showDialog, handleAgree, handleDisagree, numCampaigns } = props;

  const [maxDate, setMaxDate] = useState<Date | null>(null);
  const [minDate, setMinDate] = useState<Date | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [dateValid, setDateValid] = useState<boolean>(true);
  const [errMsg, setErrMsg] = useState<string>('');

  // Keep typescript happy.
  const localHandleAgree = () => {
    handleAgree(selectedDate?.toISOString());
  }

  // Keep typescript happy.
  const localHandleDisagree = () => {
    handleDisagree();
  }

  const onChange = (date: Date | null) => {
    const dt: any = date;
    if (dt.isValid()) {
      setDateValid(true);
      date?.setSeconds(0);
      setSelectedDate(date);
    } else {
      setDateValid(false);
    }
  }

  useEffect(() => {
    if (showDialog) {
        // Set range of allowed times for scheduling, and set initial selected date/time to one minute from now.
        const now = new Date();
        setMinDate(now);
        setMaxDate(addWeeks(2));
        now.setSeconds(0);
        setSelectedDate(new Date((now.getTime()) + 2000 * 60));
    }
  }, [showDialog])

  useEffect(() => {
    if (!dateValid) {
      setErrMsg('Date is Invalid');
    } else if (dateError && dateError === 'minDate') {
      setErrMsg('Select Future Date');
    } else if (dateError && dateError === 'minTime') {
      setErrMsg('Select Future Time');
    } else if (dateError && dateError === 'maxDate') {
      setErrMsg('Select date no more than 2 weeks from today.');
    } else if (dateError && dateError === 'maxTime') {
      setErrMsg('Select date and time no more than 2 weeks from today.');
    } else {
      setErrMsg('');
    }
  }, [dateError, dateValid])

  useEffect(() => {
    const close = (e: any) => {
      if (e.keyCode === 27) {
        handleDisagree();
      }
    }
    document.addEventListener('keydown', close)
    return () => document.removeEventListener('keydown', close)
  }, [handleDisagree])

  return (
    <div>
      <Dialog
        data-test="dialog"
        open={showDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={cx('dialog')}
        disableEscapeKeyDown={true}
      >
        <DialogTitle id="alert-dialog-title">{`Schedule${(numCampaigns > 1) ? ' ' + numCampaigns : ''} Campaign${(numCampaigns > 1) ? 's' : ''}`}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`Select future date and time when the campaign${((numCampaigns > 1) ? 's' : '')} should be sent.`}
            <div className={cx('approx-time-label')}>
            *Time is approximate.
            </div>
          </DialogContentText>
          <div className={cx('date-picker')}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <div className={cx('date-picker-container')}>
                <DesktopDateTimePicker
                  disabled={false}
                  label="Date and Time"
                  value={selectedDate}
                  onChange={(date) => onChange(date)}
                  minDateTime={minDate}
                  maxDate={maxDate}
                  maxDateTime={maxDate}
                  onError={(err) => setDateError(err)}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      error={errMsg !== ''}
                      helperText={errMsg}
                    />
                  )}
                />
              </div>
            </LocalizationProvider>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={localHandleDisagree} data-test="agree-button" color="primary" autoFocus>
            {'Cancel'}
          </Button>
          <Button onClick={localHandleAgree} data-test="disagree-button" color="primary" disabled={errMsg !== ''}>
            {'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
}
